import { useLang, useSeo } from '@/i18n/LanguageContext';
import PageHero from '@/components/PageHero';
import CtaBand from '@/components/CtaBand';
import { Reveal } from '@/components/Reveal';
import { Target, Eye, Globe2, Users } from 'lucide-react';
import CountUp from '@/components/CountUp';

export default function About() {
  const { content } = useLang();
  useSeo(`${content.about.title} | Rodotrack`, content.about.mission);
  const c = content.about;

  return (
    <>
      <PageHero badge={c.eyebrow} title={c.title} subtitle={c.story[0]} />

      <section className="py-16 lg:py-20" data-testid="about-story">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div data-testid="about-image" className="overflow-hidden rounded-3xl">
              <img src={c.img} alt={c.title} loading="lazy" className="h-full w-full object-cover" style={{ minHeight: 380 }} />
            </div>
          </Reveal>
          <div>
            {c.story.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="mb-5 text-[15px] leading-relaxed text-brand-muted sm:text-base">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <p className="flex items-start gap-3 rounded-2xl border border-brand-line bg-brand-mist p-5 text-[14px] leading-relaxed text-brand-navy/85">
                <Users className="mt-0.5 h-5 w-5 shrink-0 text-brand-blue" />
                {c.teamText}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-16 lg:pb-20" data-testid="about-numbers">
        <div className="container-x grid grid-cols-2 gap-10 lg:grid-cols-4">
          {c.numbers.map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="border-l-2 border-brand-blue/20 pl-5">
                <p className="font-display text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl"><CountUp value={s.value} /></p>
                <p className="mt-2 text-sm leading-snug text-brand-muted">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-16 lg:pb-20" data-testid="about-mission-vision">
        <div className="container-x grid gap-5 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl border border-brand-line bg-brand-mist p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue text-white"><Target className="h-6 w-6" /></span>
              <h2 className="mt-5 font-display text-2xl font-bold text-brand-navy">{content.lang === 'es' ? 'Misión' : 'Missão'}</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-brand-muted">{c.mission}</p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-brand-line bg-brand-mist p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue text-white"><Eye className="h-6 w-6" /></span>
              <h2 className="mt-5 font-display text-2xl font-bold text-brand-navy">{content.lang === 'es' ? 'Visión' : 'Visão'}</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-brand-muted">{c.vision}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-16 lg:pb-20" data-testid="about-values">
        <div className="container-x grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {c.values.map((v, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div data-testid={`about-value-${i}`} className="h-full rounded-2xl border border-brand-line bg-white p-6">
                <span className="font-mono text-sm font-bold text-brand-cyan">0{i + 1}</span>
                <h3 className="mt-3 font-display text-lg font-bold text-brand-navy">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-muted">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-16 lg:pb-20" data-testid="about-timeline">
        <div className="container-x">
          <SectionHeadMini eyebrow={c.eyebrow} title={content.lang === 'es' ? 'Nuestra historia' : 'Nossa história'} />
          <div className="relative mt-12 space-y-8 before:absolute before:left-[7px] before:top-2 before:h-[calc(100%-16px)] before:w-px before:bg-brand-line sm:before:left-1/2">
            {c.timeline.map((t, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className={`relative flex flex-col gap-1 pl-8 sm:w-1/2 sm:pl-0 ${i % 2 === 0 ? 'sm:pr-10 sm:text-right' : 'sm:ml-auto sm:pl-10'}`} data-testid={`about-timeline-${i}`}>
                  <span className={`absolute top-1.5 h-[15px] w-[15px] rounded-full border-4 border-white bg-brand-blue shadow-[0_0_0_2px_rgba(30,159,224,0.3)] ${i % 2 === 0 ? 'left-0 sm:left-auto sm:-right-[7.5px]' : 'left-0 sm:-left-[7.5px]'}`} />
                  <span className="font-mono text-[13px] font-bold text-brand-blue">{t.date}</span>
                  <p className="text-[14.5px] leading-relaxed text-brand-muted">{t.event}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-24" data-testid="about-expansion">
        <div className="container-x">
          <Reveal>
            <div className="grain relative overflow-hidden rounded-3xl bg-brand-deep px-8 py-12 lg:px-14">
              <div className="grid-dots absolute inset-0 opacity-25" />
              <div className="relative grid items-center gap-8 lg:grid-cols-2">
                <div>
                  <h2 className="flex items-center gap-3 font-display text-2xl font-bold text-white sm:text-3xl">
                    <Globe2 className="h-7 w-7 text-brand-cyan" />
                    {c.expansion}
                  </h2>
                  <p className="mt-4 text-[15px] leading-relaxed text-white/65">{c.expansionText}</p>
                </div>
                <div data-testid="about-expansion-img" className="overflow-hidden rounded-2xl">
                  <img src={c.imgTeam} alt={c.expansion} loading="lazy" className="h-full w-full object-cover" style={{ minHeight: 240 }} />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="-mt-10"><CtaBand /></div>
    </>
  );
}

function SectionHeadMini({ eyebrow, title }) {
  return (
    <div>
      <Reveal><span className="eyebrow"><span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />{eyebrow}</span></Reveal>
      <Reveal delay={0.05}><h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl">{title}</h2></Reveal>
    </div>
  );
}